// Design System Switch Component
// Unified toggle switch component

import React, { forwardRef } from 'react';
import { 
  Switch as MuiSwitch,
  FormControlLabel,
  FormControl,
  FormHelperText,
  styled
} from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, transitionTokens } from '../../tokens';

// Styled Switch with design system tokens
const StyledSwitch = styled(MuiSwitch)(({ theme }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  return {
    width: 42,
    height: 26,
    padding: 0,
    display: 'flex',
    
    '& .MuiSwitch-switchBase': {
      padding: 2,
      transition: transitionTokens.fast,
      
      '&.Mui-checked': {
        transform: 'translateX(16px)',
        color: '#fff',
        
        '& + .MuiSwitch-track': {
          backgroundColor: colors.primary.main,
          opacity: 1,
        },
      },
      
      '&.Mui-disabled': {
        color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
        
        '& + .MuiSwitch-track': {
          opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
        },
      },
    },
    
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
    },
    
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
      opacity: 1,
      transition: transitionTokens.fast,
    },
  };
});

// Styled FormControl wrapper
const StyledFormControl = styled(FormControl)(({ theme }) => ({
  marginBottom: spacingTokens.component.sm,
}));

/**
 * Switch - Unified toggle switch component
 * 
 * @example
 * // Basic usage
 * <Switch 
 *   label="Enable notifications" 
 *   checked={enabled} 
 *   onChange={setEnabled} 
 * />
 * 
 * // With helper text
 * <Switch 
 *   label="Dark mode" 
 *   checked={darkMode} 
 *   onChange={setDarkMode}
 *   helperText="Reduces eye strain in low light"
 * />
 */
const Switch = forwardRef(({
  label,
  helperText,
  error,
  labelPlacement = 'end',
  ...props
}, ref) => {
  const switchComponent = <StyledSwitch ref={ref} {...props} />;
  
  if (!label && !helperText) {
    return switchComponent;
  }
  
  return (
    <StyledFormControl error={Boolean(error)}>
      <FormControlLabel
        control={switchComponent}
        label={label}
        labelPlacement={labelPlacement}
      />
      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </StyledFormControl>
  );
});

Switch.propTypes = {
  label: PropTypes.node,
  helperText: PropTypes.node,
  error: PropTypes.bool,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  labelPlacement: PropTypes.oneOf(['end', 'start', 'top', 'bottom']),
  // All other MUI Switch props are supported
};

Switch.displayName = 'Switch';

export default Switch;